import { useState } from 'react'
import { AGENTS, AGENT_BY_ID } from '../config/competition'
import { ago, clock } from '../core/format'
import type { Thought } from '../core/types'
import { Chip, Conviction, VendorTag } from './primitives/Bits'
import { AgentAvatar, TokenIcon } from './primitives/Media'
import { Panel } from './primitives/Panel'
import { Standby } from './primitives/Standby'

const DECISION_TONE: Record<string, 'up' | 'down' | 'warn' | 'neutral' | 'accent'> = {
  BUY: 'up',
  SELL: 'down',
  CLOSE: 'accent',
  HOLD: 'neutral',
  WATCH: 'neutral',
  ABORT: 'warn',
}

export function ThoughtStream({
  thoughts,
  now,
  standby,
}: {
  thoughts: Thought[]
  now: number
  standby: boolean
}) {
  if (standby) {
    return (
      <Panel title="REASONING STREAM" note={<span className="u-label">WHY, NOT WHAT</span>}>
        <Standby note="Each agent explains its own trades, in its own words." />
      </Panel>
    )
  }

  const [filter, setFilter] = useState<string | null>(null)
  const feed = [...thoughts]
    .reverse()
    .filter((t) => !filter || t.agentId === filter)
    .slice(0, 40)

  return (
    <Panel
      title="REASONING STREAM"
      note={<span className="u-label">WHY, NOT WHAT</span>}
      scroll
      className="thoughts"
    >
      <div className="tfilter">
          <button
            className={`tfilter__btn ${filter === null ? 'is-on' : ''}`}
            onClick={() => setFilter(null)}
          >
            ALL
          </button>
          {AGENTS.map((a) => (
            <button
              key={a.id}
              className={`tfilter__btn ${filter === a.id ? 'is-on' : ''}`}
              onClick={() => setFilter(filter === a.id ? null : a.id)}
              style={{ ['--accent' as string]: a.color }}
              title={`Only ${a.name}`}
            >
              <i style={{ background: a.color }} />
              {a.name}
            </button>
          ))}
      </div>

      {feed.length === 0 ? (
        <p className="empty">Agents have not spoken yet.</p>
      ) : (
        <ol className="tstream">
          {feed.map((t) => {
            const a = AGENT_BY_ID[t.agentId]
            return (
              <li key={t.id} className="thought" style={{ ['--accent' as string]: a?.color }}>
                <header className="thought__head">
                  <AgentAvatar agentId={t.agentId} size={20} />
                  <span className="thought__agent">{a?.name}</span>
                  {a ? <VendorTag vendor={a.vendor} /> : null}
                  <span className="thought__phase">{t.phase}</span>
                  <span className="thought__time u-num" title={clock(t.ts)}>
                    {ago(t.ts, now)}
                  </span>
                </header>

                <p className="thought__headline">{t.headline}</p>
                <p className="thought__body">{t.body}</p>

                {t.signals.length ? (
                  <ul className="thought__signals">
                    {t.signals.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                ) : null}

                <footer className="thought__foot">
                  <Chip tone={DECISION_TONE[t.decision] ?? 'neutral'}>{t.decision}</Chip>
                  {t.symbol ? (
                    <span className="thought__sym">
                      <TokenIcon symbol={t.symbol} size={13} />
                      {t.symbol}
                    </span>
                  ) : null}
                  <Conviction value={t.conviction} color={a?.color} />
                  <span className="thought__cost">
                    {t.latencyMs}ms · {(t.tokensIn / 1000).toFixed(1)}k in / {t.tokensOut} out
                  </span>
                  {t.tradeId ? <span className="thought__linked">FILLED</span> : null}
                </footer>
              </li>
            )
          })}
        </ol>
      )}
    </Panel>
  )
}
